import * as aq from 'arquero';
import { z } from 'zod';

import type { EvalResult } from '../../env.ts';
import { ShapeError, TypeMismatchError } from '../../errors.ts';
import { type AstInput, astShape, nodePos, opCallPos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { kwargPath, mapValuePath, paramPath } from '../../path.ts';
import { compileExprAsRowFn, compileProjectorLambda, requireProjectorArity, withTable } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { exprColRefs } from '../../schema/check.ts';
import { type ColumnType, type Schema, schemaFromEntries } from '../../schema/types.ts';
import { expectLambda, type Value } from '../../value.ts';
import { resolveDsArg } from './helpers.ts';

/** One (column-name, expr-AST, expr-spans-path) triple — the shape both
 *  `propagateSchema` and `referencedColumns` reduce derive's two surface
 *  forms (lambda+map, kwarg) to. SSOT for "the schema/refs view of a
 *  single derive pair". */
type DerivePair = {
  readonly name: string;
  readonly exprAst: AstInput;
  readonly exprPath: string;
};

/**
 * Decode a derive call's args into the ordered list of (col-name,
 * expr-AST, expr-path) triples, common to both surface forms:
 *
 *   - Lambda+map `(derive ds (fn [%] {:col expr …}))`: each map entry
 *     becomes a pair; expr lives at `params/1/params/1/map/<i>:<key>`.
 *   - Kwarg `(derive ds :col (fn [%] expr) …)`: each kwarg becomes a
 *     pair; expr lives at `<kwarg>/params/1`.
 *
 * Tolerant of malformed shapes — returns `[]` when the form mismatches
 * (apply-time `ShapeError` covers it).
 */
const decodeDerivePairs = (data: unknown, basePath: string): readonly DerivePair[] => {
  const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
  const kwargEntries = Object.entries(data as Record<string, unknown>).filter(([k]) => k !== 'name' && k !== 'params');
  if (params.length === 2 && kwargEntries.length === 0) {
    const fnAst = params[1] as AstInput;
    const fnShape = astShape(fnAst);
    if (fnShape.kind !== 'opCall' || fnShape.call.name !== 'fn') {
      return [];
    }
    const body = fnShape.call.params?.[1];
    const bodyShape = body === undefined ? undefined : astShape(body);
    if (bodyShape === undefined || bodyShape.kind !== 'map') {
      return [];
    }
    const bodyPath = `${paramPath(basePath, 1)}/params/1`;
    return bodyShape.entries.map((entry, i) => ({
      name: entry.k,
      exprAst: entry.v as AstInput,
      exprPath: mapValuePath(bodyPath, entry.k, i),
    }));
  }
  if (params.length === 1 && kwargEntries.length > 0) {
    return kwargEntries.flatMap(([colName, fnAst]) => {
      const argPath = kwargPath(basePath, colName);
      const fnShape = astShape(fnAst as AstInput);
      if (fnShape.kind !== 'opCall' || fnShape.call.name !== 'fn') {
        return [];
      }
      const body = fnShape.call.params?.[1];
      return body === undefined ? [] : [{ name: colName, exprAst: body as AstInput, exprPath: `${argPath}/params/1` }];
    });
  }
  return [];
};
/**
 * `(derive ds <fn>)` (lambda+map form) or
 * `(derive ds :col-name <fn> :other <fn> …)` (kwarg form) — add one
 * or more derived columns. Each function is a single-arg row predicate
 * (typically `#(…)` reader macro desugared to `(fn [%] body)`).
 *
 *   - **Lambda+map form**: the function's body MUST be a map literal
 *     whose keys are the new column names and values are per-row
 *     expressions. Multi-col projection in a single closure — every
 *     entry's expression sees the row via `%` and resolves keyword
 *     col-refs via `(:col %)`.
 *   - **Kwarg form**: each `:col-name` kwarg's value is a function
 *     returning that one column's value. Use for single-col derives or
 *     when assembling the projection programmatically.
 *
 * **Row-independent (constant) projections require `(fn [_] …)`, not
 * `#(…)`.** The `#(…)` reader macro derives its parameter list from
 * `%`/`%1`/`%2…` references in its body: a body with NO `%` reference
 * desugars to a *zero-arg* lambda, which then fails the projector
 * arity gate (`projector lambda takes 1 parameter(s); got 0`). For
 * constant values — typical with `(to-date "…")`, literal numbers, or
 * op calls that don't read columns — write the explicit form:
 *
 *   - Map: `(derive ds (fn [_] {:as_of (to-date "2026-04-30")}))`
 *   - Kwarg: `(derive ds :as_of (fn [_] (to-date "2026-04-30")))`
 *
 * Pairs are derived in declaration order, each chained as its own
 * `t.derive({name: fn})` so a later pair can reference an
 * earlier-derived column via `(:earlier-col %)`.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - Function arg / kwarg value not a `LambdaValue` → `TypeMismatchError`.
 *   - Lambda arity ≠ 1 → `ProjectorArityError`. When `got = 0` (the
 *     `#(no-%)` reader-macro trip), the static hint points at the
 *     `(fn [_] …)` constant-projection form.
 *   - Lambda+map form: body isn't a map literal → `TypeMismatchError`.
 *   - Neither form satisfied (no fn arg AND no kwargs, or both at once)
 *     → `ShapeError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * projection runs.
 */
export const derive = z
  .object({
    name: z.literal('derive'),
    // Two surface shapes: [ds] (kwarg form) or [ds, fn] (lambda+map form).
    // Catchall validates arbitrary `:col-name` kwarg values as astArg.
    params: z.union([z.tuple([astArg('<dataset>')]), z.tuple([astArg('<dataset>'), astArg('<fn>')])]),
  })
  .catchall(astArg())
  .describe(
    'Add one or more derived columns. Lambda+map form `(derive ds #({:col expr}))` projects multiple cols in one closure; kwarg form `(derive ds :col-name #(expr))` declares each column individually.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    docSignature:
      '(derive ds :col #(expr) [:col2 #(expr) …]) — kwarg form; (derive ds #({:a expr :b expr})) — lambda+map form',
    example: [
      // Map+shorthand form — row-dependent projection.
      { expr: '(-> wells (derive #({:gross (* (:qty %) (:price %))})))' },
      // Kwarg form — one column at a time.
      { expr: '(-> wells (derive :gross #(* (:qty %) (:price %))))' },
      // Constant projection — body has no `%`, so use `(fn [_] …)`;
      // `#(...)` with no `%` desugars to a zero-arg lambda and fails.
      { expr: '(-> wells (derive :as_of (fn [_] (to-date "2026-04-30"))))' },
    ],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const params = data.params as readonly AstInput[];
      const kwargEntries = Object.entries(data).filter(([k]) => k !== 'name' && k !== 'params') as readonly [
        string,
        AstInput,
      ][];

      // Cross-field invariant: exactly ONE of the two shapes.
      if (params.length === 2 && kwargEntries.length > 0) {
        throw new ShapeError(
          'derive accepts either a function arg `(derive ds #(…))` OR `:col-name #(…)` kwargs — not both',
          opCallPos(ctx.spans, ctx.path),
        );
      }
      if (params.length === 1 && kwargEntries.length === 0) {
        throw new ShapeError(
          'derive needs either a `#(…)` projector or at least one `:col-name #(…)` kwarg',
          opCallPos(ctx.spans, ctx.path),
        );
      }

      const dsAst = params[0] as AstInput;
      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(derive ds …)', ctx);

      const compiledPairs =
        params.length === 2
          ? await compileLambdaMapPairs(params[1] as AstInput, ctx)
          : await compileKwargPairs(kwargEntries, ctx);

      const derived = withTable(concrete, (t) =>
        compiledPairs.reduce<typeof t>((acc, { name, rowFn }) => acc.derive({ [name]: aq.escape(rowFn) }), t),
      );
      return { value: derived, env: ctx.env };
    },
    dataset: {
      // Each pair adds (or replaces) one column on the input schema.
      // Pairs are derived sequentially, so a later pair sees earlier-
      // derived cols — fold via `schemaOverride` on `inferExprType` so
      // the accumulating schema is what the next pair types against.
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
        const pairs = decodeDerivePairs(data, ctx.path);
        // Live schema mutated in place — each pair sees its predecessors'
        // contributions. Avoids the O(n²) rebuild that a per-iteration
        // `schemaFromEntries([...inputSchema.entries(), ...acc])` would
        // pay; `folded` only collects what this derive added so the
        // overlay below can preserve input-column order.
        const liveSchema = new Map<string, ColumnType>(ctx.inputSchema);
        const folded: (readonly [string, ColumnType])[] = [];
        for (const pair of pairs) {
          const t = ctx.inferExprType(pair.exprAst, pair.exprPath, liveSchema) as ColumnType;
          liveSchema.set(pair.name, t);
          folded.push([pair.name, t]);
        }
        // Overlay folded entries on the input schema, preserving order
        // (existing cols keep position; new cols append).
        const overlay = new Map(folded);
        const existing = Array.from(ctx.inputSchema.entries(), ([k, v]) => [k, overlay.get(k) ?? v] as const);
        const added = folded.filter(([k]) => !ctx.inputSchema.has(k));
        return schemaFromEntries([...existing, ...added]);
      },
      // Walk each pair's body for col refs — both forms surface the
      // same ref shape (exprColRefs handles bare keywords, (col …),
      // nested op calls).
      //
      // Refs naming a column defined by an earlier pair (same `derive`
      // call) are intra-derive definitions, not external usages. Filter
      // them out: the type-check pass validates refs against the verb's
      // INPUT schema, which doesn't yet contain prior-pair definitions,
      // so leaving them in would mis-flag the intentional accumulating
      // row-scope as `UnknownColumnRefError`.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const pairs = decodeDerivePairs(data, basePath);
        // A ref names a prior-pair definition either directly (bare
        // `symbolRef` → `ref.name`) or as one of its `(col …)` labels
        // (e.g. `(col % derived-col)` carries `colLabels = ['%',
        // 'derived-col']`). Both forms must be filtered.
        const namesPriorDef = (ref: ColumnRefInArg, defined: ReadonlySet<string>): boolean =>
          ref.colLabels === undefined ? defined.has(ref.name) : ref.colLabels.some((l) => defined.has(l));
        const folded = pairs.reduce<{
          readonly defined: ReadonlySet<string>;
          readonly refs: readonly ColumnRefInArg[];
        }>(
          (acc, pair) => {
            const newRefs = exprColRefs(pair.exprAst, pair.exprPath).filter((ref) => !namesPriorDef(ref, acc.defined));
            return {
              defined: new Set([...acc.defined, pair.name]),
              refs: [...acc.refs, ...newRefs],
            };
          },
          { defined: new Set<string>(), refs: [] },
        );
        return folded.refs;
      },
    },
  });

type CompiledPair = { readonly name: string; readonly rowFn: (row: Record<string, unknown>) => unknown };
/**
 * Compile the lambda+map form: eval the fn arg to a `LambdaValue` whose
 * body must be a map literal; compile each entry's value with the
 * lambda's params bound to row-identity. Arity is gated via
 * `requireProjectorArity` so the wrong-shape recovery hint stays
 * uniform with the kwarg form (which routes through the kernel).
 */
const compileLambdaMapPairs = async (fnAst: AstInput, ctx: SpecialCtx): Promise<readonly CompiledPair[]> => {
  const fnPath = paramPath(ctx.path, 1);
  const { value: fnValue } = await ctx.evalNode(fnAst, ctx.env, fnPath);
  const lambda = expectLambda(fnValue as Value, '(derive ds <fn>)', nodePos(ctx.spans, fnPath));
  requireProjectorArity(lambda, 1, opCallPos(ctx.spans, fnPath));
  const bodyShape = astShape(lambda.body);
  if (bodyShape.kind !== 'map') {
    throw new TypeMismatchError(
      `the projector lambda body (a ${bodyShape.kind})`,
      'a map literal `{:col expr …}` (the new columns)',
      '(derive ds <fn>)',
      opCallPos(ctx.spans, fnPath),
    );
  }
  return Promise.all(
    bodyShape.entries.map(async (entry, i) => ({
      name: entry.k,
      rowFn: await compileExprAsRowFn(
        entry.v,
        lambda.params,
        lambda.closureEnv,
        ctx.ops,
        ctx.identities,
        ctx.spans,
        mapValuePath(fnPath, entry.k, i),
      ),
    })),
  );
};

/**
 * Compile the kwarg form: each `:col-name <fn>` becomes one compiled
 * pair. The fn arg evaluates to a `LambdaValue` whose body compiles
 * via `compileProjectorLambda` — the kernel gates arity through
 * `requireProjectorArity`, so the `(fn [_] …)` recovery hint surfaces
 * uniformly with the lambda+map form.
 */
const compileKwargPairs = async (
  kwargEntries: readonly [string, AstInput][],
  ctx: SpecialCtx,
): Promise<readonly CompiledPair[]> =>
  Promise.all(
    kwargEntries.map(async ([colName, fnAst]) => {
      const argPath = kwargPath(ctx.path, colName);
      const { value: fnValue } = await ctx.evalNode(fnAst, ctx.env, argPath);
      const lambda = expectLambda(fnValue as Value, '(derive ds :col-name <fn>)', nodePos(ctx.spans, argPath));
      const rowFn = await compileProjectorLambda(lambda, 1, ctx.ops, ctx.identities, ctx.spans, argPath);
      return { name: colName, rowFn };
    }),
  );
